import { motion } from "framer-motion";
import {
  MapPin,
  Rocket,
  Users,
  Wrench,
  Video,
  Sparkles,
  Instagram,
} from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const OFFER_ICONS = [MapPin, Rocket, Users, Wrench, Video, Sparkles, Instagram];

export default function EventCoverage() {
  const { t } = useT();
  const offers = t("events.offers");
  const venues = t("events.venues");

  return (
    <section
      id="events"
      data-testid="events-section"
      className="tt-section relative"
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-16">
          <div className="lg:col-span-6">
            <p className="tt-overline">{t("events.overline")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
              {t("events.heading_1")}{" "}
              <em className="italic text-[var(--tt-gold)]">{t("events.heading_2")}</em>.
            </h2>
          </div>
          <p className="lg:col-span-6 text-[var(--tt-text-dim)] font-light leading-relaxed lg:text-lg lg:pt-6">
            {t("events.sub")}
          </p>
        </div>

        {/* Offers grid */}
        <div className="mb-14">
          <p className="tt-overline mb-6">{t("events.offer_title")}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {offers.map((o, i) => {
              const Icon = OFFER_ICONS[i] || MapPin;
              return (
                <motion.div
                  key={o}
                  data-testid={`event-offer-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="group relative rounded-sm border border-[var(--tt-border)] p-4 md:p-5 tt-glass transition-all duration-500 hover:border-[var(--tt-border-strong)] hover:-translate-y-1"
                >
                  <Icon size={18} className="text-[var(--tt-gold)]" strokeWidth={1.4} />
                  <p className="mt-4 font-serif text-base md:text-lg text-[var(--tt-text)] leading-tight">
                    {o}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Venues badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative rounded-sm tt-trace-border"
        >
          <div className="tt-glass p-8 md:p-10 rounded-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="tt-overline text-[var(--tt-gold)]">{t("events.badge_label")}</p>
                <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3">
                  {venues.map((v) => (
                    <span
                      key={v}
                      data-testid={`event-venue-${v.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-serif italic text-2xl md:text-3xl text-[var(--tt-text)]"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              </div>
              <a
                href="#contacto"
                data-testid="events-cta"
                className="tt-btn tt-btn-primary self-start md:self-auto whitespace-nowrap"
              >
                {t("events.cta")}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
